import { useEffect, useState } from "react"
import { Link } from "react-router-dom"
import { supabase } from "../supabaseClient"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card"
import { SkeletonDashboardCards } from "../components/ui/skeleton"
import { getActiveYear } from "../utils/activeYear"
import { getScoringConfig, ScoringConfig, calculateTeamPoints } from "../utils/scoring"

interface Profile {
  display_name: string | null
  email: string | null
}

interface Entry {
  id: string
  entry_name: string
  points: number
  is_paid: boolean
}

export default function Dashboard() {
  const [profile, setProfile] = useState<Profile | null>(null)
  const [entries, setEntries] = useState<Entry[]>([])
  const [loading, setLoading] = useState(true)
  const [activeYear, setActiveYearState] = useState<number | null>(null)
  const [scoringConfig, setScoringConfig] = useState<ScoringConfig[]>([])
  const [entryFee, setEntryFee] = useState<number>(20)
  const [payoutPlaces, setPayoutPlaces] = useState<number>(0)
  const [entriesOpen, setEntriesOpen] = useState<boolean>(true)
  const [paidEntriesCount, setPaidEntriesCount] = useState<number>(0)
  const [totalPot, setTotalPot] = useState<number>(0)
  const [bracketUpdatedAt, setBracketUpdatedAt] = useState<string | null>(null)
  const [championEntriesCount, setChampionEntriesCount] = useState<number>(0)
  const [championEntryFee, setChampionEntryFee] = useState<number>(5)
  const [championPot, setChampionPot] = useState<number>(0)

  useEffect(() => {
    async function loadData() {
      const year = await getActiveYear()
      setActiveYearState(year)
      
      // Load scoring config for display
      const config = await getScoringConfig(year)
      setScoringConfig(config)

      // Load entry fee and entries_open from years table
      const { data: yearData } = await supabase
        .from('years')
        .select('entry_fee, entries_open, champion_entry_fee')
        .eq('year', year)
        .single()
      
      if (yearData?.entry_fee) {
        setEntryFee(yearData.entry_fee)
      }
      const isEntriesOpen = yearData?.entries_open ?? true
      setEntriesOpen(isEntriesOpen)
      
      // Set champion entry fee
      const championFee = yearData?.champion_entry_fee || 5
      setChampionEntryFee(championFee)

      // Get the latest game update time for this year
      const { data: latestGame } = await supabase
        .from('games')
        .select('updated_at')
        .eq('year', year)
        .eq('is_complete', true)
        .order('updated_at', { ascending: false })
        .limit(1)
        .maybeSingle()  // ← Changed from .single()
      
      setBracketUpdatedAt(latestGame?.updated_at || null)

      // Load payout config to see how many places are paid
      const { data: payoutData } = await supabase
        .from('payout_config')
        .select('place, percentage')
        .eq('year', year)
        .order('place')

      if (payoutData && payoutData.length > 0) {
        setPayoutPlaces(payoutData.length)
      }

      // Count paid entries for pool display
      const { count: paidCount } = await supabase
        .from('entries')
        .select('*', { count: 'exact', head: true })
        .eq('year', year)
        .eq('is_paid', true)

      const paid = paidCount || 0
      const fee = yearData?.entry_fee || 20
      setPaidEntriesCount(paid)
      setTotalPot(paid * fee)
      
      // Count champion entries for champion contest
      const { count: championCount } = await supabase
        .from('champion_entries')
        .select('*', { count: 'exact', head: true })
        .eq('year', year)
      
      const championEntries = championCount || 0
      setChampionEntriesCount(championEntries)
      setChampionPot(championEntries * championFee)
      
      const { data: { user } } = await supabase.auth.getUser()
      
      if (user) {
        const { data: profileData } = await supabase
          .from('profiles')
          .select('display_name, email')
          .eq('id', user.id)
          .single()
        
        setProfile(profileData)

        // Fetch entries with their picks to calculate points
        const { data: entriesData } = await supabase
          .from('entries')
          .select(`
            id, entry_name, is_paid,
            picks:entry_picks(
              team:tournament_teams(seed, wins)
            )
          `)
          .eq('user_id', user.id)
          .eq('year', year)
          .order('created_at', { ascending: false })

        // Calculate points for each entry based on picks
        const entriesWithPoints = (entriesData || []).map((entry: any) => {
          const picks = entry.picks || []
          const totalPoints = picks.reduce((sum: number, pick: any) => {
            const team = Array.isArray(pick.team) ? pick.team[0] : pick.team
            if (!team) return sum
            return sum + calculateTeamPoints(team.seed || 1, team.wins || 0)
          }, 0)
          return {
            id: entry.id,
            entry_name: entry.entry_name,
            is_paid: entry.is_paid === true,  // Ensure boolean (handle null)
            points: totalPoints
          }
        })

        setEntries(entriesWithPoints)
      }
      setLoading(false)
    }

    loadData()
  }, [])

  const displayName = profile?.display_name || profile?.email?.split('@')[0] || 'Player'

  // Example seed for scoring display
  const exampleSeed = 6

  // Format the bracket updated timestamp
  const formatUpdatedTime = (timestamp: string | null) => {
    if (!timestamp) return null
    const date = new Date(timestamp)
    const now = new Date()
    const diffMs = now.getTime() - date.getTime()
    const diffMins = Math.floor(diffMs / 60000)
    const diffHours = Math.floor(diffMs / 3600000)
    const diffDays = Math.floor(diffMs / 86400000)

    if (diffMins < 1) return 'Just now'
    if (diffMins < 60) return `${diffMins} min${diffMins === 1 ? '' : 's'} ago`
    if (diffHours < 24) return `${diffHours} hour${diffHours === 1 ? '' : 's'} ago`
    if (diffDays < 7) return `${diffDays} day${diffDays === 1 ? '' : 's'} ago`
    
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    })
  }

  if (loading) {
    return (
      <div className="space-y-8 animate-fade-in">
        {/* Skeleton Welcome Header */}
        <div className="text-center space-y-2">
          <div className="skeleton h-8 w-64 mx-auto" />
          <div className="skeleton h-4 w-48 mx-auto" />
        </div>
        
        {/* Skeleton Stats */}
        <div className="flex justify-center gap-6">
          <div className="text-center space-y-2">
            <div className="skeleton h-8 w-12 mx-auto" />
            <div className="skeleton h-3 w-16 mx-auto" />
          </div>
          <div className="w-px bg-gray-200" />
          <div className="text-center space-y-2">
            <div className="skeleton h-8 w-16 mx-auto" />
            <div className="skeleton h-3 w-16 mx-auto" />
          </div>
        </div>
        
        {/* Skeleton Cards */}
        <SkeletonDashboardCards />
      </div>
    )
  }

  return (
    <div className="space-y-8">
      {/* Welcome Header */}
      <div className="text-center space-y-2">
        <h1 className="text-2xl sm:text-3xl font-bold text-[#1e3a5f]">
          Welcome back, <span className="text-[#F15A22]">{displayName}</span>!
        </h1>
        <p className="text-gray-500">Wright Picks {activeYear} • Pick 16 Teams • Win Big</p>
      </div>

      {/* Pool Stats */}
      <div className="flex flex-col items-center gap-4">
        {paidEntriesCount > 0 && (
          <div className="flex justify-center items-center gap-6">
            <div className="text-center min-w-[80px]">
              <div className="text-2xl font-bold text-[#1e3a5f]">{paidEntriesCount}</div>
              <div className="text-xs text-gray-500 uppercase tracking-wide">Entries Paid</div>
            </div>
            <div className="w-px h-12 bg-gray-200" />
            <div className="text-center min-w-[100px]">
              <div className="text-2xl font-bold text-green-600">${totalPot.toLocaleString()}</div>
              <div className="text-xs text-gray-500 uppercase tracking-wide">Prize Pool</div>
            </div>
          </div>
        )}

        {/* Champion Stats */}
        {championEntriesCount > 0 && (
          <div className="flex justify-center items-center gap-6">
            <div className="text-center min-w-[80px]">
              <div className="text-2xl font-bold text-[#1e3a5f]">{championEntriesCount}</div>
              <div className="text-xs text-gray-500 uppercase tracking-wide">Champion</div>
            </div>
            <div className="w-px h-12 bg-gray-200" />
            <div className="text-center min-w-[100px]">
              <div className="text-2xl font-bold text-[#F15A22]">${championPot.toLocaleString()}</div>
              <div className="text-xs text-gray-500 uppercase tracking-wide">Champion Pot</div>
            </div>
          </div>
        )}
      </div>

      {/* Last Updated - Links to Results page */}
      {bracketUpdatedAt && (
        <div className="text-center text-sm">
          <Link 
            to="/results"
            className="inline-flex items-center gap-1.5 text-gray-500 hover:text-[#1e3a5f] transition-colors group"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-gray-400 group-hover:text-[#1e3a5f]">
              <circle cx="12" cy="12" r="10"/>
              <polyline points="12 6 12 12 16 14"/>
            </svg>
            Results updated {formatUpdatedTime(bracketUpdatedAt)}
            <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-gray-400 group-hover:text-[#1e3a5f]">
              <polyline points="9 18 15 12 9 6"/>
            </svg>
          </Link>
        </div>
      )}

      {/* Quick Actions - Contextual based on entries open/closed */}
      {entriesOpen ? (
        /* ENTRIES OPEN: New Entry is the hero action */
        <div className="space-y-4">
          {/* Hero Card - New Entry */}
          <Link to="/team-picker" className="block">
            <Card className="gradient-accent border-0 shadow-lg card-interactive cursor-pointer overflow-hidden">
              <CardContent className="py-6 px-6">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
                    <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5">
                      <path d="M12 5v14M5 12h14"/>
                    </svg>
                  </div>
                  <div className="flex-1">
                    <CardTitle className="text-white text-xl mb-1">Create Your Entry</CardTitle>
                    <CardDescription className="text-white/80 text-sm">
                      Pick 16 teams to compete for the ${totalPot > 0 ? totalPot.toLocaleString() : (entryFee * 10)} prize pool
                    </CardDescription>
                  </div>
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" className="flex-shrink-0 opacity-60">
                    <polyline points="9 18 15 12 9 6"/>
                  </svg>
                </div>
              </CardContent>
            </Card>
          </Link>

          {/* Secondary Actions Grid */}
          <div className="grid grid-cols-3 gap-2">
            <Link to="/my-entries">
              <Card className="bg-white border-0 shadow-sm card-interactive cursor-pointer h-full">
                <CardContent className="py-3 px-2 text-center">
                  <div className="w-9 h-9 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-1.5">
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#1e3a5f" strokeWidth="2">
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                      <polyline points="14 2 14 8 20 8"/>
                    </svg>
                  </div>
                  <CardTitle className="text-[#1e3a5f] text-xs whitespace-nowrap">My Entries</CardTitle>
                  <CardDescription className="text-gray-500 text-xs">
                    {entries.length === 0 ? 'None yet' : entries.length}
                  </CardDescription>
                </CardContent>
              </Card>
            </Link>

            <Link to="/leaderboard">
              <Card className="bg-white border-0 shadow-sm card-interactive cursor-pointer h-full">
                <CardContent className="py-3 px-2 text-center">
                  <div className="w-9 h-9 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-1.5">
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#F15A22" strokeWidth="2">
                      <path d="M18 2H6v7a6 6 0 0 0 12 0V2Z"/>
                      <path d="M4 22h16"/>
                    </svg>
                  </div>
                  <CardTitle className="text-[#1e3a5f] text-xs whitespace-nowrap">Leaderboard</CardTitle>
                  <CardDescription className="text-gray-500 text-xs">
                    Rankings
                  </CardDescription>
                </CardContent>
              </Card>
            </Link>

            <Link to="/teams">
              <Card className="bg-white border-0 shadow-sm card-interactive cursor-pointer h-full">
                <CardContent className="py-3 px-2 text-center">
                  <div className="w-9 h-9 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-1.5">
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#1e3a5f" strokeWidth="2">
                      <circle cx="12" cy="12" r="10"/>
                      <path d="M12 2a14.5 14.5 0 0 0 0 20 14.5 14.5 0 0 0 0-20"/>
                      <path d="M2 12h20"/>
                    </svg>
                  </div>
                  <CardTitle className="text-[#1e3a5f] text-xs whitespace-nowrap">Teams</CardTitle>
                  <CardDescription className="text-gray-500 text-xs">
                    Who picked
                  </CardDescription>
                </CardContent>
              </Card>
            </Link>
          </div>
        </div>
      ) : (
        /* ENTRIES CLOSED: Leaderboard becomes the hero action */
        <div className="space-y-4">
          {/* Hero Card - Leaderboard */}
          <Link to="/leaderboard" className="block">
            <Card className="gradient-brand border-0 shadow-lg card-interactive cursor-pointer overflow-hidden">
              <CardContent className="py-6 px-6">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
                    <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#F15A22" strokeWidth="2.5">
                      <path d="M18 2H6v7a6 6 0 0 0 12 0V2Z"/>
                      <path d="M4 22h16"/>
                    </svg>
                  </div>
                  <div className="flex-1">
                    <CardTitle className="text-white text-xl mb-1">View Leaderboard</CardTitle>
                    <CardDescription className="text-white/80 text-sm">
                      See where you rank in the ${totalPot > 0 ? totalPot.toLocaleString() : 0} prize pool
                    </CardDescription>
                  </div>
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" className="flex-shrink-0 opacity-60">
                    <polyline points="9 18 15 12 9 6"/>
                  </svg>
                </div>
              </CardContent>
            </Card>
          </Link>

          {/* Secondary Actions Grid */}
          <div className="grid grid-cols-3 gap-3">
            <Link to="/my-entries">
              <Card className="bg-white border-0 shadow-sm card-interactive cursor-pointer h-full">
                <CardContent className="py-4 text-center">
                  <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-2">
                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#1e3a5f" strokeWidth="2">
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                      <polyline points="14 2 14 8 20 8"/>
                    </svg>
                  </div>
                  <CardTitle className="text-[#1e3a5f] text-sm mb-0.5">My Entries</CardTitle>
                  <CardDescription className="text-gray-500 text-xs">
                    {entries.length === 0 ? 'None' : entries.length}
                  </CardDescription>
                </CardContent>
              </Card>
            </Link>

            <Link to="/teams">
              <Card className="bg-white border-0 shadow-sm card-interactive cursor-pointer h-full">
                <CardContent className="py-4 text-center">
                  <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-2">
                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#1e3a5f" strokeWidth="2">
                      <circle cx="12" cy="12" r="10"/>
                      <path d="M12 2a14.5 14.5 0 0 0 0 20 14.5 14.5 0 0 0 0-20"/>
                      <path d="M2 12h20"/>
                    </svg>
                  </div>
                  <CardTitle className="text-[#1e3a5f] text-sm mb-0.5">Teams</CardTitle>
                  <CardDescription className="text-gray-500 text-xs">
                    Who picked
                  </CardDescription>
                </CardContent>
              </Card>
            </Link>

            <Link to="/champion">
              <Card className="bg-white border-0 shadow-sm card-interactive cursor-pointer h-full">
                <CardContent className="py-4 text-center">
                  <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-2">
                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#F15A22" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6"/>
                      <path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18"/>
                      <path d="M4 22h16"/>
                      <path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22"/>
                      <path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22"/>
                      <path d="M18 2H6v7a6 6 0 0 0 12 0V2Z"/>
                      <circle cx="12" cy="6" r="2"/>
                    </svg>
                  </div>
                  <CardTitle className="text-[#1e3a5f] text-sm mb-0.5">Champion</CardTitle>
                  <CardDescription className="text-gray-500 text-xs">
                    {championEntriesCount > 0 ? `$${championPot.toLocaleString()} pot` : 'Side pot'}
                  </CardDescription>
                </CardContent>
              </Card>
            </Link>
          </div>
        </div>
      )}

      {/* How to Play & Scoring - Combined */}
      <Card className="bg-white border-0 shadow-md">
        <CardHeader className="pb-3">
          <CardTitle className="text-[#1e3a5f]">How to Play</CardTitle>
        </CardHeader>
        <CardContent className="pt-0 space-y-4">
          {/* Steps */}
          <div className="space-y-2">
            <div className="flex items-start gap-3">
              <span className="text-[#F15A22] font-bold text-sm mt-0.5">1.</span>
              <div>
                <span className="font-medium text-[#1e3a5f]">Pick 16 Teams</span>
                <span className="text-gray-500 text-sm ml-1">— any 16 from the bracket</span>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <span className="text-[#F15A22] font-bold text-sm mt-0.5">2.</span>
              <div>
                <span className="font-medium text-[#1e3a5f]">Pay ${entryFee.toLocaleString()}</span>
                <span className="text-gray-500 text-sm ml-1">— goes to the prize pool</span>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <span className="text-[#F15A22] font-bold text-sm mt-0.5">3.</span>
              <div>
                <span className="font-medium text-[#1e3a5f]">Win Prizes</span>
                <span className="text-gray-500 text-sm ml-1">— {payoutPlaces > 0 
                  ? `top ${payoutPlaces} split the pot`
                  : 'payout structure coming soon'
                }</span>
              </div>
            </div>
          </div>

          {/* Scoring Table */}
          {scoringConfig.length > 0 && (
            <div className="pt-2">
              {/* Section header with formula */}
              <div className="flex items-center justify-between mb-2">
                <span className="text-lg font-semibold text-[#1e3a5f]">Scoring</span>
                <span className="text-xs text-gray-400">
                  <span className="text-[#F15A22]">Seed</span> × <span className="text-[#F15A22]">Multiplier</span> = Points
                </span>
              </div>
              
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                {/* Table Header */}
                <div className="flex items-center justify-between bg-gray-50 px-3 py-2 text-xs font-medium text-gray-500 uppercase tracking-wide">
                  <span>Round</span>
                  <div className="flex items-center gap-4 sm:gap-8">
                    <span className="w-12 text-center">Mult.</span>
                    <span className="w-16 sm:w-20 text-right">6 Seed</span>
                  </div>
                </div>
                
                {/* Table Rows */}
                <div className="divide-y divide-gray-100">
                  {scoringConfig.map(config => (
                    <div key={config.round} className="flex items-center justify-between px-3 py-3">
                      <span className="text-sm text-gray-700 font-medium">{config.round_name}</span>
                      <div className="flex items-center gap-4 sm:gap-8">
                        <span className="w-12 text-center text-sm font-bold text-[#F15A22]">{config.multiplier}×</span>
                        <div className="w-16 sm:w-20 text-right">
                          <span className="text-sm font-semibold text-[#1e3a5f]">{exampleSeed * config.multiplier} pts</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              
                {/* Total Row - Fixed color for better cohesion */}
                <div className="bg-[#1e3a5f] text-white px-3 py-3 flex items-center justify-between">
                  <span className="text-sm">
                    <span className="hidden sm:inline">If </span>{exampleSeed} seed wins all
                  </span>
                  <span className="text-base font-bold">
                    {scoringConfig.reduce((sum, c) => sum + (exampleSeed * c.multiplier), 0)} pts
                  </span>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

